// ============================================================================
// MemberProfilePage — profil d'un MEMBRE (L16, F-810..F-824) — /profile/:email
// ============================================================================
// Portage de (dashboard)/profile/[id]/page.tsx au standard nav maison :
// BackLink contextuel -> ProfileSummaryHeader -> ProfileTabNav -> contenu.
//
// M-800 (self-redirect) : viser SON PROPRE email renvoie vers /profile en
// PRESERVANT `?tab=`. Le serveur repond `self:true` (il ne peut pas emettre un 302
// que le SPA suivrait) et la navigation se fait ICI, en `replace` (le retour
// arriere ne doit pas re-boucler sur la redirection).
// F-811 : 404 si le profil est introuvable.
// F-812 : `available` vient du SERVEUR (les 4 `can*`, M-801) — JAMAIS recalcule ici
//         (lecon GA-05). Un onglet absent n'a meme pas eu sa donnee lue.
// F-813 : BackLink contextuel `resolveProfileBack` (whitelist anti open-redirect).
// F-814/F-815 : matricule / hireDate deja masques A LA SOURCE par le serveur.
// F-817 : les onglets preservent `from`/`fromTeam` (profileBackQuery).
//
// ⚠ NT-L16-03 : StrengthsOverview d'un TIERS est rendu derriere la garde management
// (`can.strengths`) — le PoC le rendait SANS AUCUNE garde (F-819). Divergence
// ASSUMEE et gravee : ne pas reproduire une fuite PII de traits psychometriques.
// Le serveur ne sert deja pas les valeurs sans le droit ; on masque aussi le bloc
// pour ne pas afficher deux EmptyStates trompeurs (« aucune force renseignee »
// alors qu'il y en a — on ne ment pas au lecteur, on ne montre simplement rien).

import { Navigate, useParams, useSearchParams, Link } from 'react-router-dom';
import { EmptyState, QueryError, SkeletonCard, SkeletonLine, GS_COMPONENTS } from '@gs/ui';
import { Activity, ArrowLeft, FileText, ShieldAlert } from 'lucide-react';
import { ApiForbiddenError, ApiServerError } from '@gs/api-client';
import { useAuth } from '@gs/auth';
import { useTranslation } from '../../i18n';
import { useMemberProfile } from '../../hooks/useProfile';
import { ProfileTabNav, resolveProfileTab } from '../../components/profile/ProfileTabNav';
import { ProfileSummaryHeader } from '../../components/profile/ProfileSummaryHeader';
import { OverviewStats } from '../../components/profile/OverviewStats';
import { StrengthsOverview } from '../../components/profile/StrengthsOverview';
import { CompensationSection } from '../../components/profile/CompensationSection';
import { ManagementTab } from '../../components/employee/ManagementTab';
import { resolveProfileBack, profileBackQuery } from '../../lib/nav/back-context';
import { ProfileForbidden, ComingSoonTab } from './shared';

function ProfileSkeleton() {
  return (
    <div className="space-y-4">
      <SkeletonLine className="h-4 w-48" />
      <SkeletonCard />
      <SkeletonCard />
    </div>
  );
}

export default function MemberProfilePage() {
  const { t } = useTranslation();
  const { email: rawEmail } = useParams<{ email: string }>();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();

  const from = searchParams.get('from');
  const fromTeam = searchParams.get('fromTeam');
  const rawTab = searchParams.get('tab');

  const q = useMemberProfile(rawEmail, !!user);

  const notFound = q.isError && q.error instanceof ApiServerError && q.error.status === 404;
  const forbidden = q.isError && q.error instanceof ApiForbiddenError;

  // M-800 : self-redirect, `?tab=` preserve. `replace` -> pas de boucle au retour.
  if (q.data?.self) {
    return <Navigate to={rawTab ? `/profile?tab=${encodeURIComponent(rawTab)}` : '/profile'} replace />;
  }

  if (q.isLoading) return <ProfileSkeleton />;
  if (forbidden) return <ProfileForbidden />;
  if (notFound) {
    // F-811 : profil introuvable.
    return (
      <EmptyState
        icon={<ShieldAlert size={48} aria-hidden="true" />}
        title={t('gest:profile.notFound.title')}
        description={t('gest:profile.notFound.description')}
        action={
          <Link to="/organigramme" className={GS_COMPONENTS.btnSecondary}>
            {t('gest:profile.notFound.back')}
          </Link>
        }
      />
    );
  }
  if (q.isError) return <QueryError message={t('gest:profile.loadError')} onRetry={() => void q.refetch()} />;
  if (!q.data?.profile) return null;

  const { profile, tabs, can, overview, compensation } = q.data;
  const tab = resolveProfileTab(rawTab, tabs);
  // F-813 : whitelist — un `from` inconnu/non porte donne null (aucun BackLink).
  const back = resolveProfileBack(from, fromTeam);
  const extraQuery = profileBackQuery(from, fromTeam);

  return (
    <div className="space-y-5">
      {back ? (
        <Link
          to={back.href}
          className="mb-2 inline-flex touch-manipulation items-center gap-1.5 text-meta font-medium text-slate-500 transition-colors hover:text-slate-700 dark:hover:text-slate-300"
        >
          <ArrowLeft size={14} aria-hidden="true" />
          {t(back.labelKey)}
        </Link>
      ) : null}

      <ProfileSummaryHeader
        eyebrow={t('gest:profile.eyebrow.member')}
        fullName={profile.fullName}
        email={profile.email}
        // Badge du role de la PERSONNE affichee (F-825) — pas celui du viewer.
        role={profile.systemCRole === 'ADMIN' ? 'admin' : profile.systemCRole === 'GESTIONNAIRE' ? 'manager' : 'employee'}
        committee={profile.committee}
        jobTitle={profile.jobTitle}
        teamName={profile.teamName}
        matricule={profile.matricule}
        hireDate={compensation?.hireDate ?? null}
        responsable={profile.responsable}
      >
        {/* F-816 : description affichee si renseignee. AUCUN formulaire ici : le
            lot n'offre aucune edition d'un tiers (M-807). */}
        {profile.tasksDescription ? (
          <p className="whitespace-pre-wrap text-field text-slate-600 dark:text-slate-300">
            {profile.tasksDescription}
          </p>
        ) : null}
      </ProfileSummaryHeader>

      <ProfileTabNav
        basePath={`/profile/${encodeURIComponent(profile.email)}`}
        active={tab}
        available={tabs}
        extraQuery={extraQuery}
      />

      {tab === 'apercu' ? (
        <div className="flex flex-col gap-6">
          {/* F-818 : OverviewStats seulement si canReadMemberPulse. */}
          {can?.pulse && overview ? (
            <OverviewStats
              memberPulses={overview.pulses}
              objectives={overview.objectives}
              hireDate={compensation?.hireDate ?? null}
            />
          ) : null}
          {/* NT-L16-03 : garde AJOUTEE (management) — le PoC n'en avait aucune. */}
          {can?.strengths ? (
            <StrengthsOverview strengths={profile.gallupStrengths} nova={profile.nova} />
          ) : null}
        </div>
      ) : null}

      {tab === 'pouls' && can?.pulse ? <ComingSoonTab icon={<Activity size={48} aria-hidden="true" />} lot="pulse" /> : null}
      {tab === 'documents' && can?.documents ? <ComingSoonTab icon={<FileText size={48} aria-hidden="true" />} lot="documents" /> : null}
      {/* F-822 : CompensationSection seulement si canReadCompensation. */}
      {tab === 'remuneration' && can?.compensation ? <CompensationSection compensation={compensation} /> : null}
      {/* F-750 (L09) : ManagementTab quand canManageEmployee. `gestion` n'est dans
          `tabs` (serveur) que si canManage, et le hub /api/management re-verifie —
          l'onglet reflete l'etat, il ne decide pas (M-750). */}
      {tab === 'gestion' && can?.manage ? <ManagementTab email={profile.email} /> : null}
    </div>
  );
}
